import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Driver, ColumnDefinition } from "../types/driver.types";

interface DriverTableProps {
  drivers: Driver[];
  selectedDrivers: Driver[];
  onSelectionChange: (e: { value: Driver[] }) => void;
  onEdit: (driver: Driver) => void;
  onDelete: (driver: Driver) => void;
  onResetPassword: (driverId: string) => void;
  columns: ColumnDefinition[];
  isLoading: boolean;
}

export const DriverTable: React.FC<DriverTableProps> = ({
  drivers,
  selectedDrivers,
  onSelectionChange,
  onEdit,
  onDelete,
  onResetPassword,
  columns,
  isLoading,
}) => {
  const actionBodyTemplate = (rowData: Driver) => {
    return (
      <div className="flex flex-nowrap space-x-2">
        <Button
          icon="pi pi-refresh"
          className="p-button-rounded p-button-info bg-primary text-white border-none hover:-translate-y-1 transition-all ease-in-out duration-300"
          onClick={() => rowData._id && onResetPassword(rowData._id)}
          title="Réinitialiser le mot de passe"
          disabled={!rowData._id}
          tooltip="Réinitialiser le mot de passe"
          tooltipOptions={{ position: "top", updateDelay: 0, className: "font-sans text-center -translate-y-3" }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success bg-primary border-none hover:-translate-y-1 transition-all ease-in-out duration-300"
          onClick={() => onEdit(rowData)}
          title="Modifier le chauffeur"
          disabled={!rowData._id}
          tooltip="Modifier le chauffeur"
          tooltipOptions={{ position: "top", updateDelay: 0, className: "font-sans text-center -translate-y-3" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-warning bg-red-400 text-white mr-2 border-none hover:-translate-y-1 transition-all ease-in-out duration-300"
          onClick={() => onDelete(rowData)}
          title="Supprimer le chauffeur"
          disabled={!rowData._id}
          tooltip="Supprimer le chauffeur"
          tooltipOptions={{ position: "top", updateDelay: 0, className: "font-sans text-center -translate-y-3" }}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: Driver, field: keyof Driver) => {
    return new Date(rowData[field] as string).toLocaleDateString("fr");
  };

  const activeStatusBodyTemplate = (rowData: Driver) => {
    const isActive = rowData.manuallyActive;
    const statusClass = isActive ? "bg-green-500" : "bg-red-500";
    return (
      <span className={`${statusClass} text-white px-2 py-1 rounded-full text-sm`}>
        {isActive ? "Actif" : "Inactif"}
      </span>
    );
  };

  return (
    <div className="bg-white p-4 mt-2 rounded-xl h-full">
      <DataTable
        value={drivers}
        selection={selectedDrivers}
        onSelectionChange={onSelectionChange}
        dataKey="_id"
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} chauffeurs"
        paginatorClassName="border-none font-sans text-center"
        loading={isLoading}
        className="rounded-xl overflow-clip h-full flex flex-col justify-between font-sans"
        emptyMessage="Vous n'avez pas encore de chauffeurs" scrollable style={{ width: "100%" }}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        {columns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            sortable
            body={
              col.field === "creationDate" || col.field === "birthDate"
                ? (rowData) => dateBodyTemplate(rowData, col.field as keyof Driver)
                : col.field === "manuallyActive"
                ? activeStatusBodyTemplate
                : undefined
            }
            className={`${col.field === "email" ? "w-[200px] text-ellipsis overflow-hidden" : "w-[140px]"}`}
          />
        ))}
        <Column header="Opérations" body={actionBodyTemplate} style={{ width: "10rem" }} />
      </DataTable>
    </div>
  );
};
